import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
    HiChevronDoubleLeft,
    HiChevronDoubleRight,
    HiChevronLeft,
    HiChevronRight,
} from 'react-icons/hi2';
import { darken, lighten } from 'polished';
import theme from '@styles/theme';
import StackIcon from '@assets/icons/stackIcon.svg';
import SelectBoxArrow from '@assets/icons/selectboxArrow.png';

type PaginationProps = {
    pageGroupLength: number;
    pageIndex: number;
    take: number;
    totalLength: number;
    setTake: React.Dispatch<React.SetStateAction<number>>;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    prevPageHandler: () => void;
    nextPageHandler: () => void;
    prevPageGroupHandler: () => void;
    nextPageGroupHandler: () => void;
    resetPaginationHandler: () => void;
};

const PaginationV2 = ({
    pageGroupLength,
    pageIndex,
    take,
    totalLength,
    setTake,
    setPageIndex,
    prevPageHandler,
    nextPageHandler,
    prevPageGroupHandler,
    nextPageGroupHandler,
    resetPaginationHandler,
}: PaginationProps) => {
    // 페이지네이션 셀렉트박스 show, hide
    const [isOpen, setIsOpen] = useState(false);
    const outRef = useRef<HTMLUListElement>(null);

    const lengthStartNum = pageGroupLength + pageIndex + 1;
    const lengthEndNum =
        totalLength < pageGroupLength + pageIndex + take
            ? totalLength
            : pageGroupLength + pageIndex + take;

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            // 해당 이벤트가 영역 밖이라면
            if (outRef.current && !outRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [setIsOpen]);

    return (
        <PaginationArea>
            {/* 총 수량 보이는 영역 */}
            <PageInfoContainer>
                <PageInfoText>번호</PageInfoText>
                <PageInfo>{`${lengthStartNum} - ${lengthEndNum} of ${
                    totalLength ? totalLength : 0
                }`}</PageInfo>
                <Image src={StackIcon} />
                <TakeSelectBox
                    name="pageAmount"
                    value={take}
                    onChange={(e) => {
                        setTake(Number(e.target.value));
                        resetPaginationHandler();
                    }}
                >
                    <Option value={10}>10</Option>
                    <Option value={20}>20</Option>
                    <Option value={30}>30</Option>
                </TakeSelectBox>
            </PageInfoContainer>

            {/* 이동 버튼 영역 */}
            <PaginationContainer>
                <PaginationBtn onClick={prevPageGroupHandler}>
                    <HiChevronDoubleLeft
                        color={`${theme.colors.disabled}`}
                        size={20}
                    />
                </PaginationBtn>
                <PaginationBtn onClick={prevPageHandler}>
                    <HiChevronLeft
                        color={`${theme.colors.disabled}`}
                        size={20}
                    />
                </PaginationBtn>
                <PageSelectBox onClick={() => setIsOpen(!isOpen)}>
                    {(pageGroupLength + pageIndex + take) / take}
                </PageSelectBox>
                <PaginationBtn onClick={nextPageHandler}>
                    <HiChevronRight
                        color={`${theme.colors.disabled}`}
                        size={20}
                    />
                </PaginationBtn>
                <PaginationBtn onClick={nextPageGroupHandler}>
                    <HiChevronDoubleRight
                        color={`${theme.colors.disabled}`}
                        size={20}
                    />
                </PaginationBtn>

                {/* 페이지네이션 셀렉트박스 */}
                {isOpen && (
                    <PaginationListContainer ref={outRef}>
                        {pageGroupLength !== 0 ? (
                            <PaginationList
                                key="prev"
                                onClick={prevPageGroupHandler}
                            >
                                이전
                            </PaginationList>
                        ) : (
                            <></>
                        )}
                        {[...Array(10)].map((_, i) => (
                            <React.Fragment
                                key={pageGroupLength / take + 1 + i}
                            >
                                {pageGroupLength / take + 1 + i <=
                                    Math.ceil(totalLength / take) && (
                                    <PaginationList
                                        onClick={() => {
                                            setPageIndex(i * take);
                                            setIsOpen(!isOpen);
                                        }}
                                    >
                                        {pageGroupLength / take + 1 + i}
                                    </PaginationList>
                                )}
                            </React.Fragment>
                        ))}
                        {totalLength - take * 10 <= pageGroupLength ? (
                            <></>
                        ) : (
                            <PaginationList
                                key="next"
                                onClick={nextPageGroupHandler}
                            >
                                다음
                            </PaginationList>
                        )}
                    </PaginationListContainer>
                )}
            </PaginationContainer>
        </PaginationArea>
    );
};

export default PaginationV2;

const PaginationArea = styled.section`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 20px 0 100px;
`;

const PageInfoContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    font-family: AppleSDGothicNeoBold, sans-serif;
`;

const PageInfoText = styled.span`
    display: inline-block;
    margin-right: 12px;
    color: ${theme.colors.disabled};
`;

const PageInfo = styled.span`
    display: inline-block;
    margin-right: 12px;
`;

const TakeSelectBox = styled.select`
    margin-left: 12px;
    padding: 0 12px;
    width: 60px;
    height: 32px;
    border: ${theme.colors.paginationBorder} solid 1px;
    border-radius: 4px;
    background: url(${SelectBoxArrow}),
        ${theme.colors.whiteColor} no-repeat center;
    font-family: AppleSDGothicNeoBold, sans-serif;
    cursor: pointer;
    &:hover {
        background-color: ${lighten(0.01, theme.colors.whiteColor)};
    }

    &:active {
        background-color: ${darken(0.01, theme.colors.whiteColor)};
    }
`;

const Option = styled.option``;

const PaginationContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    font-family: AppleSDGothicNeoBold, sans-serif;
`;

const PaginationBtn = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    border: ${theme.colors.paginationBorder} solid 1px;
    background-color: ${theme.colors.whiteColor};
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: ${darken(0.01, theme.colors.whiteColor)};
    }

    &:active {
        background-color: ${darken(0.02, theme.colors.whiteColor)};
    }
`;

const PageSelectBox = styled.span`
    margin: 0 12px;
    padding: 8px 12px;
    width: 60px;
    height: 32px;
    border: ${theme.colors.paginationBorder} solid 1px;
    border-radius: 4px;
    background: url(${SelectBoxArrow}) no-repeat center;
    cursor: pointer;
`;
const PaginationListContainer = styled.ul`
    position: absolute;
    top: 0;
    right: 76px;
    z-index: 10;
    width: 60px;
    border: ${theme.colors.paginationBorder} solid 1px;
    border-radius: 4px;
    background-color: ${theme.colors.whiteColor};
`;
const PaginationList = styled.li`
    padding: 8px 10px;
    width: 100%;
    background-color: ${theme.colors.whiteColor};
    cursor: pointer;
    &:hover {
        background-color: ${darken(0.01, theme.colors.whiteColor)};
    }

    &:active {
        background-color: ${darken(0.02, theme.colors.whiteColor)};
    }
`;

const Image = styled.img`
    display: block;
`;
